import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import ProductCard from "../components/ProductCard";
import { useToast } from "../hooks/use-toast";
import { getProduct, getProducts } from "../lib/api";
import Seo from "../components/Seo";

const US_SIZE_EQUIVALENCES = {
  "5/5.5": "36",
  "6": "37",
  "6.5/7": "38",
  "7": "40",
  "7.5/8": "39",
  "8": "41",
  "8.5/9": "42",
  "9": "39",
  "9.5": "43",
  "10": "44",
  "11": "41",
  "12": "42",
};
const FREE_SHIPPING_MIN = 200000;
const FLAT_SHIPPING_COST = 10000;
const SHOW_SHIPPING = true;

const Carrito = () => {
  const [carrito, setCarrito] = useState([]);
  const [recomendaciones, setRecomendaciones] = useState([]);
  const [crossSell, setCrossSell] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [whatsappOnlyMap, setWhatsappOnlyMap] = useState({});
  const [stockLoading, setStockLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const carritoGuardado = JSON.parse(localStorage.getItem("carrito") || "[]");
    setCarrito(carritoGuardado);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const ids = Array.from(new Set(carrito.map((item) => item.productoId))).filter(Boolean);
    if (!ids.length) {
      setStockMap({});
      setWhatsappOnlyMap({});
      return;
    }
    setStockLoading(true);
    Promise.allSettled(ids.map((id) => getProduct(id)))
      .then((results) => {
        if (!isMounted) return;
        const nextMap = {};
        const whatsappMap = {};
        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            const product = result.value || {};
            nextMap[ids[index]] = product.stock ?? 0;
            whatsappMap[ids[index]] = Boolean(product.whatsapp_only);
          }
        });
        setStockMap(nextMap);
        setWhatsappOnlyMap(whatsappMap);
      })
      .finally(() => {
        if (isMounted) setStockLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [carrito]);

  useEffect(() => {
    if (!Object.keys(stockMap).length) return;
    let changed = false;
    const updated = [];
    const remaining = { ...stockMap };
    const warnings = new Set();
    carrito.forEach((item) => {
      const available = remaining[item.productoId];
      if (available === undefined) {
        updated.push(item);
        return;
      }
      if (available <= 0) {
        changed = true;
        warnings.add(item.nombre);
        return;
      }
      const allowed = Math.min(item.cantidad, available);
      if (allowed < item.cantidad) {
        changed = true;
        warnings.add(item.nombre);
      }
      if (allowed > 0) {
        updated.push({ ...item, cantidad: allowed });
        remaining[item.productoId] = available - allowed;
      }
    });
    if (changed) {
      if (warnings.size) {
        toast({
          title: "Stock insuficiente",
          description: `Ajustamos cantidades por stock: ${Array.from(warnings).join(", ")}.`,
          variant: "destructive",
        });
      }
      setCarrito(updated);
      localStorage.setItem("carrito", JSON.stringify(updated));
    }
  }, [stockMap, carrito, toast]);

  useEffect(() => {
    let isMounted = true;
    getProducts({ limit: 4 })
      .then((data) => {
        if (isMounted) setRecomendaciones(data);
      })
      .catch(() => {});
    getProducts({ category: "accesorios", limit: 4 })
      .then((data) => {
        if (isMounted) setCrossSell(data);
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  const formatoPrecio = (precio) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(precio);
  };

  const actualizarCantidad = (index, nuevaCantidad) => {
    if (nuevaCantidad < 1) return;
    const item = carrito[index];
    const available = stockMap[item?.productoId];
    if (available !== undefined) {
      const requestedTotal = carrito.reduce((sum, current, idx) => {
        if (current.productoId !== item.productoId) return sum;
        if (idx === index) return sum + nuevaCantidad;
        return sum + current.cantidad;
      }, 0);
      if (requestedTotal > available) {
        toast({
          title: "Stock insuficiente",
          description: `${item.nombre} solo tiene ${available} unidades disponibles.`,
          variant: "destructive",
        });
        return;
      }
    }
    const nuevoCarrito = [...carrito];
    nuevoCarrito[index].cantidad = nuevaCantidad;
    setCarrito(nuevoCarrito);
    localStorage.setItem("carrito", JSON.stringify(nuevoCarrito));
  };

  const eliminarItem = (index) => {
    const nuevoCarrito = carrito.filter((_, i) => i !== index);
    setCarrito(nuevoCarrito);
    localStorage.setItem("carrito", JSON.stringify(nuevoCarrito));
  };

  const calcularSubtotal = () => {
    return carrito.reduce(
      (total, item) => total + Number(item.precio) * item.cantidad,
      0,
    );
  };

  const totalByProduct = carrito.reduce((acc, item) => {
    acc[item.productoId] = (acc[item.productoId] || 0) + item.cantidad;
    return acc;
  }, {});

  const parseSizeLabel = (value) => {
    if (!value) return { us: null, eu: null, cm: null };
    const text = String(value);
    const usMatch = text.match(/US\s*([0-9./]+)/i);
    const euMatch = text.match(/EUR\s*([0-9./]+)/i);
    const cmMatch = text.match(/CM\s*([0-9./]+)/i);
    return {
      us: usMatch ? usMatch[1] : null,
      eu: euMatch ? euMatch[1] : null,
      cm: cmMatch ? cmMatch[1] : null,
    };
  };

  const getTallaLabel = (item) => {
    if (!item?.talla) return "";
    const parsed = parseSizeLabel(item.talla);
    const usValue = parsed.us || item.talla;
    const tallaEu =
      parsed.eu || item.tallaEu || item.tallaCo || US_SIZE_EQUIVALENCES[item.talla];
    const cmValue = parsed.cm;
    if (!tallaEu && !cmValue) return `Talla US: ${usValue}`;
    if (tallaEu && cmValue) return `Talla US: ${usValue} (EUR ${tallaEu}, CM ${cmValue})`;
    if (tallaEu) return `Talla US: ${usValue} (EUR ${tallaEu})`;
    return `Talla US: ${usValue} (CM ${cmValue})`;
  };

  const subtotal = calcularSubtotal();
  const envio = SHOW_SHIPPING ? (subtotal >= FREE_SHIPPING_MIN ? 0 : FLAT_SHIPPING_COST) : 0;
  const descuento = 0;
  const total = subtotal - descuento + envio;
  const hasInsufficientStock = carrito.some((item) => {
    const available = stockMap[item.productoId];
    if (available === undefined) return false;
    return totalByProduct[item.productoId] > available;
  });
  const whatsappOnlyItems = carrito.filter(
    (item) => whatsappOnlyMap[item.productoId],
  );
  const hasWhatsappOnlyItems = whatsappOnlyItems.length > 0;
  const whatsappOnlyMessage = whatsappOnlyItems.length
      ? [
        "Hola, quiero comprar por WhatsApp:",
        ...whatsappOnlyItems.map((item) => {
          const tallaLabel = item.talla ? ` | ${getTallaLabel(item)}` : "";
          return `- ${item.nombre} x${item.cantidad}${tallaLabel}`;
        }),
      ].join("\n")
    : "";

  if (carrito.length === 0) {
    return (
      <div className="min-h-screen">
        <Seo title="Carrito" robots="noindex, nofollow" />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold mb-4">Tu carrito esta vacio</h1>
            <p className="text-gray-600 mb-8">Aun no has agregado productos a tu carrito</p>
            <Button onClick={() => navigate("/catalogo/todos")}>Ver productos</Button>
          </div>

          {recomendaciones.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold mb-6">Nuevo en la tienda</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {recomendaciones.map((producto) => (
                  <ProductCard key={producto.id} producto={producto} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo title="Carrito" robots="noindex, nofollow" />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Carrito de Compras</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {carrito.map((item, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <img
                      src={item.imagen}
                      alt={item.nombre}
                      className="w-24 h-24 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold mb-2">{item.nombre}</h3>
                      {item.talla && (
                        <p className="text-sm text-gray-500">{getTallaLabel(item)}</p>
                      )}
                      <p className="font-bold text-lg">{formatoPrecio(item.precio)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <Button variant="ghost" size="icon" onClick={() => eliminarItem(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => actualizarCantidad(index, item.cantidad - 1)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-semibold">{item.cantidad}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => actualizarCantidad(index, item.cantidad + 1)}
                          disabled={
                            stockMap[item.productoId] !== undefined &&
                            totalByProduct[item.productoId] >= stockMap[item.productoId]
                          }
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      {stockMap[item.productoId] !== undefined && (
                        <p className="text-xs text-gray-500">
                          Stock disponible: {stockMap[item.productoId]}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4">Resumen del Pedido</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold">{formatoPrecio(subtotal)}</span>
                  </div>
                  {descuento > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Cupon 5%:</span>
                      <span className="font-semibold">-{formatoPrecio(descuento)}</span>
                    </div>
                  )}
                  {SHOW_SHIPPING ? (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Envio:</span>
                      <span className="font-semibold">
                        {envio === 0 ? "Gratis" : formatoPrecio(envio)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Envio:</span>
                      <span className="font-semibold">Se calcula en checkout</span>
                    </div>
                  )}
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span>{formatoPrecio(total)}</span>
                    </div>
                  </div>
                </div>
                <Button
                  className="w-full mb-3"
                  onClick={() => navigate("/checkout")}
                  disabled={hasInsufficientStock || stockLoading || hasWhatsappOnlyItems}
                >
                  Finalizar Compra
                </Button>
                {hasInsufficientStock && (
                  <p className="text-xs text-red-600 text-center">
                    Ajusta cantidades para continuar.
                  </p>
                )}
                {hasWhatsappOnlyItems && (
                  <>
                    <p className="text-xs text-emerald-700 text-center">
                      Este carrito tiene productos solo por WhatsApp.
                    </p>
                    <Button
                      variant="outline"
                      className="w-full mt-3"
                      onClick={() =>
                        window.open(
                          `https://wa.me/573184215616?text=${encodeURIComponent(whatsappOnlyMessage)}`,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      Asesoria y Compra por WhatsApp
                    </Button>
                  </>
                )}
                <Button variant="outline" className="w-full" onClick={() => navigate("/catalogo/todos")}>
                  Seguir Comprando
                </Button>
                <p className="mt-4 text-xs text-gray-500 text-center">
                  Envio gratis desde $200.000. Envio $10.000 en compras menores. Recibe un cupon del 5% en compras superiores a $200.000.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {crossSell.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold mb-6">Tal vez te pueda interesar</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {crossSell.map((producto) => (
                <ProductCard key={producto.id} producto={producto} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Carrito;
